import {
  SingleCourseTrafficProps,
  UseSingleCourseTrafficConfig,
  UseSingleCourseTraffic,
  SingleCourseTrafficTestResult,
  SingleCourseTrafficVariant,
} from "./types";
import { useMonarchRule } from "../MonarchRulesProvider";
import { ProcessedCourse } from "@/common/course/types";

const CUSTOM_RULE_ID = "singleCourseDetailPageRedesign";
const generatePayload = (course?: ProcessedCourse) => {
  return {
    courseModes: course?.activeCourseRun?.seats
      ?.map((seat) => seat?.type)
      .join(","),
  };
};

const useSingleCourseTraffic: UseSingleCourseTraffic = ({ course } = {}) => {
  const { isLoading, value } = useMonarchRule<{
    data: SingleCourseTrafficProps;
    variantCustomId: string;
  }>(CUSTOM_RULE_ID, generatePayload(course));

  // If still loading or no value returned, return loading state
  if (isLoading || !value) {
    return {
      isLoading,
    } as SingleCourseTrafficTestResult;
  }

  // Determine the variant based on the returned data
  const variant: SingleCourseTrafficVariant =
    (value.data.displayExperience as SingleCourseTrafficVariant) ||
    "redesignV1";
  // Extract data for the experiment
  const data: SingleCourseTrafficProps = value.data;

  // Return the experiment result with proper typing
  return {
    isLoading: false,
    variant,
    data,
  };
};

// Hook for configuration data access
export const useSingleCourseTrafficConfig: UseSingleCourseTrafficConfig = ({
  course,
} = {}) => {
  const { value, isLoading } = useMonarchRule<{
    data: SingleCourseTrafficProps;
    variantCustomId: string;
  }>(CUSTOM_RULE_ID, generatePayload(course));

  return {
    displayExperience: value?.data?.displayExperience,
    isLoading,
    headerBackground: value?.data?.headerBackground,
    sectionsOrder: value?.data?.sectionsOrder,
    straightToCheckout: value?.data?.straightToCheckout,
    showHeaderPrice: value?.data?.showHeaderPrice,
    showHeaderPriceMobile: value?.data?.showHeaderPriceMobile,
    headerPriceLabel: value?.data?.headerPriceLabel,
    headerPricePosition: value?.data?.headerPricePosition,
    currencySelector: value?.data?.currencySelector,
    simpleMobileHeader: value?.data?.simpleMobileHeader,
    showHeaderFactoidBar: value?.data?.showHeaderFactoidBar,
    factoidsBar: value?.data?.factoidsBar,
    showRedesignedTrackComparison: value?.data?.showRedesignedTrackComparison,
    emphasisModuleVersion: value?.data?.emphasisModuleVersion,
    information: value?.data?.information,
    stats: value?.data?.stats,
    testimonial: value?.data?.testimonial,
    tableDescription: value?.data?.tableDescription,
    pathInfo: value?.data?.pathInfo,
    tabsOrder: value?.data?.tabsOrder,
    hidePrice: value?.data?.hidePrice,
    relatedPrograms: value?.data?.relatedPrograms,

    promoBanner: value?.data?.promoBanner,
    productCompare: value?.data?.productCompare,
    carouselData: {
      title: value?.data?.relatedProgramsRedesign?.design1?.title,
      extraInfo: value?.data?.relatedProgramsRedesign?.design1?.extraInfo,
      tagValue: value?.data?.relatedProgramsRedesign?.design1?.tagValue,
      tagIcon: value?.data?.relatedProgramsRedesign?.design1?.tagIcon,
      backgroundColor:
        value?.data?.relatedProgramsRedesign?.design1?.backgroundColor,
      designToDisplay: value?.data?.relatedProgramsRedesign?.designToDisplay,
      highlightText:
        value?.data?.relatedProgramsRedesign?.design1?.highlightText,
    },
    carouselDataDesign3: {
      title:
        value?.data?.relatedProgramsRedesign?.design3?.carouselSection?.title,
      description:
        value?.data?.relatedProgramsRedesign?.design3?.carouselSection
          ?.description,
      backgroundColor:
        value?.data?.relatedProgramsRedesign?.design3?.carouselSection
          ?.backgroundColor,
      highlightText:
        value?.data?.relatedProgramsRedesign?.design3?.carouselSection
          ?.highlightText,
    },
    relatedProgramsRedesign: {
      design2: {
        title: value?.data?.relatedProgramsRedesign?.design2?.title ?? "",
        subtitle: value?.data?.relatedProgramsRedesign?.design2?.subtitle,
        reasons: value?.data?.relatedProgramsRedesign?.design2?.reasons,
        showMoreLabel:
          value?.data?.relatedProgramsRedesign?.design2?.showMoreLabel,
        showLessLabel:
          value?.data?.relatedProgramsRedesign?.design2?.showLessLabel,
        backgroundColor:
          value?.data?.relatedProgramsRedesign?.design2?.backgroundColor,
        descriptionFeaturedProgramsAppend:
          value?.data?.relatedProgramsRedesign?.design2
            ?.descriptionFeaturedProgramsAppend,
      },
      designToDisplay: value?.data?.relatedProgramsRedesign?.designToDisplay,
      cardType: value?.data?.relatedProgramsRedesign?.cardType,
    },
    mainContent: value?.data?.mainContent,
    collapsableContent: value?.data?.collapsableContent,
    collapsableContentLabel: value?.data?.collapsableContentLabel,
    stepComponent: value?.data?.stepComponent,
    showSideRail: value?.data?.showSideRail,
    sideRailTitle: value?.data?.sideRailTitle,
    sideRailBulletList: value?.data?.sideRailBulletList,
    sideRailStatisticComponent: value?.data?.sideRailStatisticComponent,
    sideRailImage: value?.data?.sideRailImage,
    sideRailImageTitle: value?.data?.sideRailImageTitle,
    sideRailHeaderComponents: value?.data?.sideRailHeaderComponents,
    sideRailBodyComponents: value?.data?.sideRailBodyComponents,
    sideRailCollapsedHeaderComponents:
      value?.data?.sideRailCollapsedHeaderComponents,
    sideRailCollapsedBodyComponents:
      value?.data?.sideRailCollapsedBodyComponents,
    sideRailMobileHeaderComponents: value?.data?.sideRailMobileHeaderComponents,
    sideRailMobileBodyComponents: value?.data?.sideRailMobileBodyComponents,
    sideRailProductTitle: value?.data?.sideRailProductTitle,
    sideRailRating: value?.data?.sideRailRating,
    sideRailVideoPlayer: value?.data?.sideRailVideoPlayer,
    sideRailSkillsList: value?.data?.sideRailSkillsList,
    partnershipDescription: value?.data?.partnershipDescription,
    courseBenefitList: value?.data?.courseBenefitList,
    countdownBanner: value?.data?.countdownBanner,
    alumniTestimonials: value?.data?.alumniTestimonials,
    highlights: value?.data?.highlights,
    highlightsAce: value?.data?.highlightsAce,
    verificationUpsellBanner: value?.data?.verificationUpsellBanner,
    verifiedBadge: value?.data?.verifiedBadge,
    statsBanner: value?.data?.statsBanner,
    benefitsModule: value?.data?.benefitsModule,
    testimonialsModule: value?.data?.testimonialsModule,
    bundleSavingsBanner: value?.data?.bundleSavingsBanner,
    insertBadges: value?.data?.insertBadges,
    showDynamicBadges: value?.data?.showDynamicBadges,
    careerCards: value?.data?.careerCards,
    partnerLogo: value?.data?.partnerLogo,
    showHeaderRibbon: value?.data?.showHeaderRibbon,
    headerRibbonText: value?.data?.headerRibbonText,
    headerBackgroundImageUrl: value?.data?.headerBackgroundImageUrl,
  };
};

export default useSingleCourseTraffic;
